import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Box,
  Divider,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Header } from 'components/Header/Header';
import ReportsService from './serviceReports';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

interface FormState {
  frequency: 'daily' | 'weekly' | 'monthly';
  time: {
    hour: number;
    minute: number;
  } | null;
  emails: string | string[];
  id_submerchant: string;
  enabled: boolean;
}
interface ApiTimeFormat {
  hour: number;
  minute: number;
}

interface ApiResponse {
  data: {
    emails: string | string[];
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    id_submerchant: number;
    time: ApiTimeFormat;
  };
}

interface ReportSectionProps {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onReset: () => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

const defaultFormState: FormState = {
  frequency: 'daily',
  time: null,
  emails: '',
  id_submerchant: '52',
  enabled: true,
};

interface DisableConfirmationProps {
  open: boolean;
  type: 'payment' | 'activity';
  onClose: () => void;
  onConfirm: () => void;
}

const DisableConfirmationModal: React.FC<DisableConfirmationProps> = ({
  open,
  type,
  onClose,
  onConfirm,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby='disable-dialog-title'
    aria-describedby='disable-dialog-description'
  >
    <DialogTitle id='disable-dialog-title'>
      Disable {type === 'payment' ? 'Payment' : 'Activity'} Report?
    </DialogTitle>
    <DialogContent>
      <DialogContentText id='disable-dialog-description'>
        Are you sure you want to disable the automated {type === 'payment' ? 'payment' : 'activity'}{' '}
        report? This will stop all scheduled reports until re-enabled.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color='primary'>
        Cancel
      </Button>
      <Button onClick={onConfirm} color='error' variant='contained'>
        Disable
      </Button>
    </DialogActions>
  </Dialog>
);

const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  description,
  enabled,
  onToggle,
  form,
  setForm,
  onReset,
  onSave,
  saving,
}) => {
  const handleFrequencyChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFrequency: 'daily' | 'weekly' | 'monthly'
  ) => {
    if (newFrequency !== null) {
      setForm((prev) => ({ ...prev, frequency: newFrequency }));
    }
  };

  const handleTimeChange = (newTime: Date | null) => {
    setForm((prev) => ({
      ...prev,
      time: newTime ? { hour: newTime.getHours(), minute: newTime.getMinutes() } : null,
    }));
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, emails: event.target.value }));
  };

  return (
    <Box
      sx={{
        maxWidth: 'md',
        mx: 'auto',
        p: 3,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant='h6' gutterBottom>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {description}
          </Typography>
        </div>
        <Switch checked={enabled} onChange={(e) => onToggle(e.target.checked)} color='primary' />
      </Box>

      {!enabled && (
        <Alert icon={<Info />} severity='info' sx={{ mb: 3 }}>
          Enable automated reports to receive regular updates about your system activity and status.
        </Alert>
      )}

      {enabled && (
        <Box component='form' sx={{ '& .MuiTextField-root': { mb: 3 }, mt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' gutterBottom>
              Report Frequency
            </Typography>
            <ToggleButtonGroup
              value={form.frequency}
              exclusive
              onChange={handleFrequencyChange}
              fullWidth
            >
              <ToggleButton value='daily'>Daily</ToggleButton>
              <ToggleButton value='weekly'>Weekly</ToggleButton>
              <ToggleButton value='monthly'>Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              label='Report Time'
              value={form.time ? new Date(0, 0, 0, form.time.hour, form.time.minute) : null}
              onChange={handleTimeChange}
              sx={{ width: '100%', mb: 3 }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label='Report Recipients'
            value={form.emails}
            onChange={handleEmailChange}
            placeholder='Enter email addresses separated by commas'
            helperText='Add multiple emails separated by commas'
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant='outlined' onClick={onReset}>
              Reset
            </Button>
            <Button
              variant='contained'
              onClick={onSave}
              disabled={!form.time || !form.emails || saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const transformFormToApiFormat = (form: FormState): ApiResponse['data'] => {
  return {
    enabled: false,
    frequency: form.frequency,
    emails:
      typeof form.emails === 'string'
        ? form.emails.split(',').map((email) => email.trim())
        : form.emails,
    id_submerchant: parseInt(form.id_submerchant, 10),
    time: form.time ? { hour: form.time.hour, minute: form.time.minute } : { hour: 0, minute: 0 },
  };
};

const ReportSettings: React.FC = () => {
  // Payment Report State
  const [reportPayment, setReportPayment] = useState<boolean>(false);
  const [paymentForm, setPaymentForm] = useState<FormState>(defaultFormState);
  const [savingPayment, setSavingPayment] = useState<boolean>(false);

  // Activity Report State
  const [reportActivity, setReportActivity] = useState<boolean>(false);
  const [activityForm, setActivityForm] = useState<FormState>(defaultFormState);
  const [savingActivity, setSavingActivity] = useState<boolean>(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableType, setDisableType] = useState<'payment' | 'activity'>('payment');

  const handleToggleChange = (type: 'payment' | 'activity', checked: boolean) => {
    if (!checked) {
      // If trying to disable, show confirmation modal
      setDisableType(type);
      setDisableModalOpen(true);
    } else {
      // If enabling, just update the state
      if (type === 'payment') {
        setReportPayment(true);
        setPaymentForm((prev) => ({ ...prev, enabled: true }));
      } else {
        setReportActivity(true);
        setActivityForm((prev) => ({ ...prev, enabled: true }));
      }
    }
  };

  const handleDisableConfirm = async () => {
    try {
      if (disableType === 'payment') {
        setSavingPayment(true);
        await ReportsService.saveReportsPayment({
          ...transformFormToApiFormat(paymentForm),
          enabled: false,
        });
        setReportPayment(false);
        setSavingPayment(false);
      } else {
        setSavingActivity(true);
        await ReportsService.saveReportsActivity({
          ...transformFormToApiFormat(activityForm),
          enabled: false,
        });
        setReportActivity(false);
        setSavingActivity(false);
      }
    } catch (error) {
      console.error('Error disabling report:', error);
      // Handle error (show error message, etc.)
    } finally {
      setDisableModalOpen(false);
    }
  };

  useEffect(() => {
    const fetchReportSettings = async () => {
      try {
        const responsePayment = (await ReportsService.getReportsPayments()) as ApiResponse;
        const responseActivity = (await ReportsService.getReportsActivity()) as ApiResponse;

        // Actualizar los toggles
        setReportPayment(responsePayment.data.enabled);
        setReportActivity(responseActivity.data.enabled);

        // Función auxiliar para transformar el formato de la API al formato del formulario
        const transformApiResponse = (apiData: ApiResponse['data']): FormState => {
          // Crear objeto Date con la hora y minutos de la API
          const time = apiData.time
            ? new Date(0, 0, 0, apiData.time.hour, apiData.time.minute)
            : null;

          // Manejar emails que pueden venir como string o array
          const emails =
            typeof apiData.emails === 'string'
              ? apiData.emails // Si es string, lo dejamos como está
              : Array.isArray(apiData.emails)
              ? apiData.emails.join(', ') // Si es array, lo unimos con comas
              : ''; // Por si acaso viene undefined o null

          return {
            enabled: apiData.enabled,
            frequency: apiData.frequency,
            // Convertir el string de emails a array deber ser ["email1", "email2", ...]
            emails: Array.isArray(emails) ? emails : emails.split(',').map((email) => email.trim()),
            time: time ? { hour: time.getHours(), minute: time.getMinutes() } : null,
            id_submerchant: '52',
          };
        };

        // Actualizar los formularios con los datos transformados
        setPaymentForm(transformApiResponse(responsePayment.data));
        setActivityForm(transformApiResponse(responseActivity.data));
      } catch (error) {
        console.error('Error fetching report settings:', error);
        // Aquí puedes manejar el error como prefieras (mostrar un mensaje, etc.)
      }
    };

    fetchReportSettings();
  }, []);

  // Payment Report Methods
  const onToggleChangePayment = (checked: boolean) => {
    setReportPayment(checked);
  };

  const resetPaymentForm = () => {
    setPaymentForm(defaultFormState);
  };

  const savePaymentReport = async () => {
    setSavingPayment(true);
    // Simulate API call
    console.log(paymentForm);
    await ReportsService.saveReportsPayment(paymentForm);
    setSavingPayment(false);
  };

  // Activity Report Methods
  const onToggleChangeActivity = (checked: boolean) => {
    setReportActivity(checked);
  };

  const resetActivityForm = () => {
    setActivityForm(defaultFormState);
  };

  const saveActivity = async () => {
    setSavingActivity(true);
    // Simulate API call
    console.log(activityForm);
    await ReportsService.saveReportsActivity(activityForm);
    setSavingActivity(false);
  };

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
      <Header />
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <ReportSection
            title='Payment Report Settings'
            description='Configure your automated payment reports'
            enabled={reportPayment}
            onToggle={(checked) => handleToggleChange('payment', checked)}
            form={paymentForm}
            setForm={setPaymentForm}
            onReset={resetPaymentForm}
            onSave={savePaymentReport}
            saving={savingPayment}
          />

          <Divider sx={{ my: 4 }} />

          <ReportSection
            title='System Activity Report'
            description='Configure your automated activity reports'
            enabled={reportActivity}
            onToggle={(checked) => handleToggleChange('activity', checked)}
            form={activityForm}
            setForm={setActivityForm}
            onReset={resetActivityForm}
            onSave={saveActivity}
            saving={savingActivity}
          />
        </CardContent>
      </Card>
      <DisableConfirmationModal
        open={disableModalOpen}
        type={disableType}
        onClose={() => setDisableModalOpen(false)}
        onConfirm={handleDisableConfirm}
      />
    </Box>
  );
};

export default ReportSettings;
